import React, { useMemo } from 'react';
import { format, isToday, startOfDay } from 'date-fns';
import { ArrowPathIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { dayKey, eventsByDay, safeParse } from './calendarDateUtils';
import { colorForEvent, EVENT_COLOR_CLASSES } from './eventColors';
import type { CalendarEvent } from '../../types/integrations';

interface CalendarDayViewProps {
  cursorDate: Date;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onDayClick?: (date: Date) => void;
}

const HOUR_HEIGHT = 56;
const DAY_HEIGHT = 24 * HOUR_HEIGHT;

const positionEvent = (event: CalendarEvent, day: Date) => {
  if (event.all_day) return null;
  const dayStart = startOfDay(day).getTime();
  const dayEnd = dayStart + 24 * 60 * 60 * 1000;
  const start = Math.max(safeParse(event.start).getTime(), dayStart);
  const end = Math.min(safeParse(event.end).getTime(), dayEnd);
  if (end <= start) return null;
  return {
    top: ((start - dayStart) / (60 * 60 * 1000)) * HOUR_HEIGHT,
    height: Math.max(28, ((end - start) / (60 * 60 * 1000)) * HOUR_HEIGHT - 2),
  };
};

const CalendarDayView: React.FC<CalendarDayViewProps> = ({
  cursorDate,
  events,
  onEventClick,
  onDayClick,
}) => {
  const eventMap = useMemo(() => eventsByDay(events), [events]);
  const dayEvents = eventMap.get(dayKey(cursorDate)) ?? [];
  const allDay = dayEvents.filter((e) => e.all_day);
  const timed = dayEvents.filter((e) => !e.all_day);

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const today = isToday(cursorDate);

  return (
    <div className="flex flex-col h-[700px] overflow-hidden">
      {/* Day header */}
      <div
        className="grid border-b border-border-primary/15"
        style={{ gridTemplateColumns: '80px 1fr' }}
      >
        <div className="border-r border-border-primary/15" />
        <div className="px-3 py-3 flex items-center gap-3">
          <div
            className={[
              'inline-flex items-center justify-center min-w-[36px] h-9 rounded-full text-base font-semibold',
              today ? 'bg-error-500 text-white' : 'text-text-primary',
            ].join(' ')}
          >
            {format(cursorDate, 'd')}
          </div>
          <div>
            <div className="text-sm font-bold uppercase tracking-wider text-text-secondary">
              {format(cursorDate, 'EEEE')}
            </div>
            <div className="text-xs text-text-tertiary">
              {format(cursorDate, 'MMMM yyyy')}
            </div>
          </div>
        </div>
      </div>

      {/* All-day row */}
      {allDay.length > 0 && (
        <div
          className="grid border-b border-border-primary/15 bg-surface-primary"
          style={{ gridTemplateColumns: '80px 1fr' }}
        >
          <div className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-text-tertiary border-r border-border-primary/15 flex items-center">
            All day
          </div>
          <div className="p-2 flex flex-wrap gap-2">
            {allDay.map((e) => {
              const c = EVENT_COLOR_CLASSES[colorForEvent(e.id)];
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => onEventClick?.(e)}
                  className={[
                    'text-xs font-medium px-2 py-1 rounded-1 active:scale-[0.97] transition-transform',
                    c.pill,
                    e.is_tereiko ? 'border-l-2 border-primary-500' : '',
                  ].join(' ')}
                >
                  {e.summary || 'Untitled event'}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Time grid */}
      <div className="flex-1 overflow-y-auto">
        <div
          className="grid relative"
          style={{ gridTemplateColumns: '80px 1fr', height: DAY_HEIGHT }}
        >
          <div className="border-r border-border-primary/15">
            {hours.map((h) => (
              <div
                key={h}
                style={{ height: HOUR_HEIGHT }}
                className="text-[11px] text-text-tertiary text-right pr-2 -translate-y-2"
              >
                {h === 0 ? '' : format(new Date(2000, 0, 1, h), 'h a').toLowerCase()}
              </div>
            ))}
          </div>
          <div
            className={['relative', today ? 'bg-primary-500/4' : ''].join(' ')}
            onClick={() => onDayClick?.(cursorDate)}
          >
            {hours.map((h) => (
              <div
                key={h}
                style={{ height: HOUR_HEIGHT }}
                className="border-t border-border-primary/10 first:border-t-0"
              />
            ))}
            {timed.map((event) => {
              const pos = positionEvent(event, cursorDate);
              if (!pos) return null;
              const c = EVENT_COLOR_CLASSES[colorForEvent(event.id)];
              return (
                <button
                  key={event.id}
                  type="button"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onEventClick?.(event);
                  }}
                  style={{ top: pos.top, height: pos.height }}
                  className={[
                    'absolute left-2 right-2 rounded-2 px-3 py-2 text-left overflow-hidden shadow-sm',
                    'active:scale-[0.99] transition-transform',
                    c.pill,
                    event.is_tereiko ? 'border-l-2 border-primary-500' : '',
                  ].join(' ')}
                >
                  <div className="text-[11px] font-bold">
                    {format(safeParse(event.start), 'h:mma').toLowerCase().replace(/m$/, '')}
                    {' – '}
                    {format(safeParse(event.end), 'h:mma').toLowerCase().replace(/m$/, '')}
                  </div>
                  <div className="text-sm font-semibold mt-0.5 truncate flex items-center gap-1.5">
                    <span className="truncate">{event.summary || 'Untitled event'}</span>
                    {event.is_recurring && <ArrowPathIcon className="w-3 h-3 opacity-70 shrink-0" />}
                    {event.meeting_link && <VideoCameraIcon className="w-3 h-3 opacity-70 shrink-0" />}
                  </div>
                  {event.location && (
                    <div className="text-xs text-text-secondary mt-0.5 truncate">
                      {event.location}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarDayView;
